import { CollapsibleFilters } from '../../common/CollapsibleFilters';
import React, { useState } from "react";
import {
  Alert,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import type { EmailCampaign } from "../../../types/marketing";
import { ResponsiveTable } from "../../common/ResponsiveTable";
import { AudienceSelector } from "./AudienceSelector";
import {
  allEmailRows,
  emailError,
  type AudienceSegment,
} from "./emailWorkflows";
export function AudiencesTab() {
  const [editing, setEditing] = useState<Partial<EmailCampaign> | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState<{
    count: number;
    exclusion_reasons: Record<string, number>;
  } | null>(null);
  const cache = useQueryClient();
  const audiences = useQuery({
    queryKey: ["email-audiences"],
    queryFn: () =>
      allEmailRows<AudienceSegment>("/api/marketing/email-audiences/"),
  });
  const save = async () => {
    if (!editing) return;
    setBusy(true);
    setError("");
    try {
      const payload = {
        name: editing.name,
        audience_type: editing.audience_type,
        audience_contact_type: editing.audience_contact_type,
        audience_tags: editing.audience_tags || [],
        manual_contacts: editing.manual_contacts || [],
        excluded_contacts: editing.excluded_contacts || [],
      };
      if (editing.id)
        await api.patch(
          `/api/marketing/email-audiences/${editing.id}/`,
          payload,
        );
      else await api.post("/api/marketing/email-audiences/", payload);
      await cache.invalidateQueries({ queryKey: ["email-audiences"] });
      setEditing(null);
    } catch (cause) {
      setError(emailError(cause));
    }
    setBusy(false);
  };
  return (
    <Stack spacing={2}>
      <Typography level="body-sm">
        Save reusable criteria for future campaigns. Counts update as contacts
        change; scheduled campaigns retain their reviewed audience.
      </Typography>
      <Button
          onClick={() =>
            setEditing({
              name: "",
              audience_type: "all",
              audience_contact_type: "",
              audience_tags: [],
              manual_contacts: [],
              excluded_contacts: [],
            })
          }
        >
          New audience
        </Button>
<CollapsibleFilters><Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Input
          aria-label="Search audiences"
          placeholder="Search saved audiences"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

      </Stack></CollapsibleFilters>
      {error && <Alert color="danger">{error}</Alert>}
      {audiences.isError && (
        <Alert
          color="danger"
          endDecorator={
            <Button onClick={() => audiences.refetch()}>Retry</Button>
          }
        >
          Saved audiences unavailable.
        </Alert>
      )}
      <ResponsiveTable
        rows={(audiences.data || []).filter((row) =>
          row.name.toLowerCase().includes(search.toLowerCase()),
        )}
        getRowKey={(row) => row.id}
        columns={[
          {
            key: "name",
            header: "Audience",
            mobileVariant: "header",
            render: (row) => row.name,
          },
          {
            key: "type",
            header: "Criteria",
            render: (row) => row.audience_type.replace("_", " "),
          },
        ]}
        actionsWidth="300px"
        rowActions={(row) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="plain"
              onClick={() =>
                setEditing({
                  id: row.id,
                  name: row.name,
                  audience_type: row.audience_type,
                  audience_contact_type: row.audience_contact_type,
                  audience_tags: row.audience_tag_ids || [],
                  manual_contacts: row.manual_contact_ids || [],
                  excluded_contacts: row.excluded_contact_ids || [],
                })
              }
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="plain"
              onClick={async () => {
                try {
                  setPreview(
                    (
                      await api.get<{
                        count: number;
                        exclusion_reasons: Record<string, number>;
                      }>(`/api/marketing/email-audiences/${row.id}/preview/`)
                    ).data,
                  );
                } catch (cause) {
                  setError(emailError(cause));
                }
              }}
            >
              Preview count
            </Button>
            <Button
              size="sm"
              variant="plain"
              color="danger"
              disabled={busy}
              onClick={async () => {
                if (
                  !window.confirm(
                    `Remove saved audience “${row.name}”? Draft campaigns using it will need a new audience selection.`,
                  )
                )
                  return;
                setBusy(true);
                try {
                  await api.delete(`/api/marketing/email-audiences/${row.id}/`);
                  await cache.invalidateQueries({
                    queryKey: ["email-audiences"],
                  });
                } catch (cause) {
                  setError(emailError(cause));
                }
                setBusy(false);
              }}
            >
              Remove
            </Button>
          </Stack>
        )}
        pagination={{ pageSize: 20, itemNoun: "audiences" }}
      />
      {preview && (
        <Alert
          endDecorator={
            <Button size="sm" variant="plain" onClick={() => setPreview(null)}>
              Dismiss
            </Button>
          }
        >
          {preview.count} eligible contacts.{" "}
          {Object.entries(preview.exclusion_reasons)
            .map(([reason, count]) => `${reason}: ${count}`)
            .join(" · ")}
        </Alert>
      )}
      <Modal
        open={!!editing}
        onClose={() => {
          if (!busy && window.confirm("Discard unsaved audience changes?"))
            setEditing(null);
        }}
      >
        <ModalDialog
          sx={{
            width: 750,
            maxWidth: "95vw",
            maxHeight: "92vh",
            overflowY: "auto",
          }}
        >
          <Typography level="h4">Saved audience</Typography>
          {error && <Alert color="danger">{error}</Alert>}
          {editing && (
            <>
              <FormControl>
                <FormLabel>Audience name</FormLabel>
                <Input
                  value={editing.name || ""}
                  onChange={(event) =>
                    setEditing({ ...editing, name: event.target.value })
                  }
                />
              </FormControl>
              <AudienceSelector
                allowSegments={false}
                draft={editing}
                onChange={(patch) => setEditing({ ...editing, ...patch })}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="plain"
                  disabled={busy}
                  onClick={() => setEditing(null)}
                >
                  Cancel
                </Button>
                <Button
                  loading={busy}
                  disabled={!editing.name?.trim()}
                  onClick={save}
                >
                  Save audience
                </Button>
              </Stack>
            </>
          )}
        </ModalDialog>
      </Modal>
    </Stack>
  );
}
